import { QueryObserverResult, UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { getEpicsByPortfolioId } from '../portfolio-client/portfolio-client';
import { getEpicsByProjectId, getEpicsByProjectIdAndSubprojectId } from '../projects-client/projects-client';
import type { Epic } from '../tasks-client/task-client.type';
import {
  getProcessGraph,
  getProcessMapping,
  getProcessTaskTypes,
  getProcessTaskTypesWithOptions,
  getProcessTasks,
} from './process-client';
import type {
  GraphData,
  ProcessGraphConfig,
  ProcessMapping,
  ProcessTask,
  ProcessTasksConfig,
  ProcessTasksData,
  ProcessTaskTypeOption,
  ProcessTaskTypesData,
  Stages,
} from './process-client.type';

/**
 * Query manager for fetching process map graph data
 *
 * @param {ProcessGraphConfig} config - Configuration object for the query
 * @param {UseQueryOptions<GraphData>} options - Additional options for the query (optional)
 * @return {{ graphData: GraphData, query: QueryObserverResult<GraphData> }} - An object containing the graph data and the query
 */
const useProcessGraph = ({
  teamId,
  workflowId,
  boardId,
  workPeriodId,
  taskTypes,
  epicId,
  taskId,
  graphType,
  startDate,
  endDate,
  useBloomfilterSections,
  options,
}: ProcessGraphConfig & {
  options?: UseQueryOptions<GraphData>;
}): { graphData: GraphData | undefined; query: QueryObserverResult<GraphData> } => {
  const query = useQuery({
    queryKey: [
      'process-graph',
      teamId,
      workflowId,
      boardId,
      workPeriodId,
      taskTypes,
      epicId,
      taskId,
      graphType,
      startDate,
      endDate,
    ] as const,
    queryFn: () => {
      if (teamId || workflowId) {
        return getProcessGraph({
          project_id: teamId,
          workflow_id: workflowId,
          subproject_id: boardId,
          work_period_id: workPeriodId,
          task_types: taskTypes,
          epic_id: epicId,
          task_id: taskId,
          graph_type: graphType,
          start_date: startDate,
          end_date: endDate,
          bloomfilter_sections: useBloomfilterSections,
        });
      }
      return Promise.reject('Cannot get process graph data: either teamId or workflowId is required');
    },
    ...options,
  });

  return { graphData: query.data, query };
};

/**
 * Query manager for fetching bf/customer stages mapping data.
 *
 * When BOTH `teamId` and `workflowId` are provided, the workflow-scoped
 * endpoint is preferred because TRI-591 only ships `labels` (per-workflow
 * `bucket_label_overrides`) on the workflow-scoped response. The
 * project-scoped fallback is retained for callers that only have a team
 * id (e.g. team-overview surfaces).
 *
 * @param {string | undefined} teamId - the team id (project-scoped fallback)
 * @param {string | undefined} workflowId - the workflow id (preferred when present)
 * @param {UseQueryOptions<ProcessMapping>} options - additional options for the query (optional)
 * @returns {{ mapping: ProcessMapping | undefined; query: QueryObserverResult<ProcessMapping> }}
 */
const useProcessMapping = ({
  teamId,
  workflowId,
  options,
}: {
  teamId?: string;
  workflowId?: string;
  // The hook owns `queryKey` and `queryFn`; omit them from the caller-
  // facing options so we cannot drift from the internal cache key.
  options?: Omit<UseQueryOptions<ProcessMapping>, 'queryKey' | 'queryFn'>;
}): { mapping: ProcessMapping | undefined; query: QueryObserverResult<ProcessMapping> } => {
  const query = useQuery({
    queryKey: ['process-mapping', teamId, workflowId] as const,
    queryFn: () => {
      // Prefer workflow-scoped fetch so per-workflow `bucket_label_overrides`
      // reach the FE; fall back to project-scoped when the caller
      // only has a team id.
      if (workflowId) {
        return getProcessMapping(workflowId, true);
      }
      if (teamId) {
        return getProcessMapping(teamId);
      }
      return Promise.reject('Cannot get process mapping: either teamId or workflowId is required');
    },
    ...options,
  });

  return { mapping: query.data, query };
};

/**
 * Query manager for fetching process tasks
 *
 * @param {ProcessTasksConfig} config - Configuration object for the query
 * @param {Partial<UseQueryOptions<ProcessTasksData>>} options - Additional options for the query (optional)
 * @return {{ taskData: ProcessTasksData | undefined, query: QueryObserverResult<ProcessTasksData> }} - An object containing the tasks and the query result
 */
const useProcessTasks = (
  {
    teamId,
    workflowId,
    workPeriodId,
    taskTypes,
    boardId,
    epicId,
    startDate,
    endDate,
    processWorkflow,
    useGroups,
  }: ProcessTasksConfig,
  options?: Partial<UseQueryOptions<ProcessTasksData>>,
) => {
  let permutations = getCustomerStagesPermutations(processWorkflow?.mapping || ({} as Record<Stages, string[]>));
  if (!useGroups && permutations.length === 0) {
    permutations = [`${processWorkflow?.source},${processWorkflow?.destination}`];
  }

  const queries = useQueries({
    queries: permutations.map((permutation) => ({
      queryKey: [
        'process-tasks',
        teamId,
        workflowId,
        boardId,
        workPeriodId,
        taskTypes,
        epicId,
        startDate,
        endDate,
        permutation,
      ],
      queryFn: () =>
        teamId || workflowId
          ? getProcessTasks(
              teamId,
              workflowId,
              boardId,
              workPeriodId,
              taskTypes,
              epicId,
              startDate,
              endDate,
              permutation,
            )
          : Promise.reject('Cannot get process tasks: either teamId or workflowId is required'),
      ...options,
    })),
  });

  const taskData = queries.reduce((acc, queryResult) => {
    const { data: { tasks = [] } = {} } = queryResult;

    if (tasks.length > 0) {
      const filteredTasks = tasks.filter((task) => !acc.find((t) => t.id === task.id));

      return [...acc, ...filteredTasks];
    }

    return acc;
  }, [] as ProcessTask[]);

  const isFetching = queries.some((query) => query.isFetching);

  return { taskData, isFetching };
};

/**
 * Generates an array of permutations between source and destination stages based on a given mapping.
 *
 * @param {Record<Stages, string[]>} mapping - The mapping of stages.
 * @returns {string[]} - An array of permutations between source and destination stages.
 */
const getCustomerStagesPermutations = (mapping: Record<Stages, string[]>): string[] => {
  const customerStagesMappings = Object.values(mapping);
  const [sourceStages = [], destinationStages = []] = customerStagesMappings;

  return sourceStages.reduce((acc, sourceStage) => {
    destinationStages.forEach((destinationStage) => {
      acc.push(`${sourceStage},${destinationStage}`);
    });

    return acc;
  }, [] as string[]);
};

/**
 * Query manager for fetching epics for a given scope.
 *
 * @param {AtLeastOne<{ portfolioId?: string; projectId?: string; subprojectId?: string }>} ids - IDs for fetching epics; at least one of these IDs must be provided
 * @param {UseQueryOptions<Epic[]>} options - Options for the useQuery hook
 * @return {{ data: Epic[] | undefined; query: QueryObserverResult<Epic[]> }} Object containing data and query result
 */
const useEpics = (
  ids: { portfolioId: string | null; projectId: string | null; subprojectId: string | null },
  options?: UseQueryOptions<Epic[]>,
): { epics: Epic[] | undefined; query: QueryObserverResult<Epic[]> } => {
  const { portfolioId, projectId, subprojectId } = ids;
  const query = useQuery<Epic[], Error>({
    queryKey: ['epics', portfolioId, projectId, subprojectId],
    queryFn: () => {
      if (ids.subprojectId) {
        if (!ids.projectId) {
          return Promise.reject('Project ID is required if subproject ID is provided');
        }
        return getEpicsByProjectIdAndSubprojectId(ids.projectId, ids.subprojectId);
      }

      if (ids.projectId) {
        return getEpicsByProjectId(ids.projectId);
      }

      if (ids.portfolioId) {
        return getEpicsByPortfolioId(ids.portfolioId);
      }

      return Promise.reject('No valid ID provided');
    },
    ...options,
  });

  return { epics: query.data, query };
};

/**
 * Fetches distinct work item types for the scoped task set without date filtering.
 * Designed to fire in parallel with useProcessGraph so the Work Item Type dropdown
 * can be populated before the full graph response arrives.
 *
 * @param params - Scope params (project_id / workflow_id required; subproject_id and work_period_id optional)
 * @param options - Additional React Query options (e.g. enabled)
 */
const useProcessTaskTypes = (
  params: { teamId?: string; workflowId?: string; boardId?: string; workPeriodId?: string },
  options?: Partial<UseQueryOptions<string[]>>,
): { taskTypes: string[] | undefined; query: QueryObserverResult<string[]> } => {
  const { teamId, workflowId, boardId, workPeriodId } = params;
  const query = useQuery<string[]>({
    queryKey: ['process-task-types', teamId, workflowId, boardId, workPeriodId],
    queryFn: () =>
      getProcessTaskTypes({
        project_id: teamId,
        workflow_id: workflowId,
        subproject_id: boardId,
        work_period_id: workPeriodId,
      }),
    enabled: Boolean(teamId || workflowId),
    ...options,
  });
  return { taskTypes: query.data, query };
};

/**
 * Fetches distinct work item types plus their labeled options for the scoped
 * task set in ONE request to the same endpoint as `useProcessTaskTypes`.
 * Prefer this hook when the caller also needs display labels — using it
 * instead of (not alongside) `useProcessTaskTypes` keeps the HTTP call count
 * at one. Option values are exact stored raw_type keys (the graph
 * `task_types` filter values); labels carry the verbatim external casing and
 * equal the value when no label has been synced yet.
 *
 * @param {{ teamId?: string; workflowId?: string; boardId?: string; workPeriodId?: string }} params - Scope params (project_id / workflow_id required; subproject_id and work_period_id optional)
 * @param {Partial<UseQueryOptions<ProcessTaskTypesData>>} options - Additional React Query options (e.g. enabled)
 * @returns {{ taskTypes: string[] | undefined; taskTypeOptions: ProcessTaskTypeOption[] | undefined; query: QueryObserverResult<ProcessTaskTypesData> }} Legacy type list, labeled options, and the query result
 */
const useProcessTaskTypesWithOptions = (
  params: { teamId?: string; workflowId?: string; boardId?: string; workPeriodId?: string },
  options?: Partial<UseQueryOptions<ProcessTaskTypesData>>,
): {
  taskTypes: string[] | undefined;
  taskTypeOptions: ProcessTaskTypeOption[] | undefined;
  query: QueryObserverResult<ProcessTaskTypesData>;
} => {
  const { teamId, workflowId, boardId, workPeriodId } = params;
  const query = useQuery<ProcessTaskTypesData>({
    queryKey: ['process-task-types-with-options', teamId, workflowId, boardId, workPeriodId],
    queryFn: () =>
      getProcessTaskTypesWithOptions({
        project_id: teamId,
        workflow_id: workflowId,
        subproject_id: boardId,
        work_period_id: workPeriodId,
      }),
    enabled: Boolean(teamId || workflowId),
    ...options,
  });
  return { taskTypes: query.data?.taskTypes, taskTypeOptions: query.data?.taskTypeOptions, query };
};

export {
  useEpics,
  useProcessGraph,
  useProcessMapping,
  useProcessTaskTypes,
  useProcessTaskTypesWithOptions,
  useProcessTasks,
};
